import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Users, Wallet, Loader2, X, Search, Pencil } from "lucide-react";

export const CustomersPanel = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = users.filter((u) =>
    !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="customers-panel">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" /><h2 className="font-heading font-bold text-slate-900">Customer Accounts</h2></div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="customers-search"
            className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 w-56" />
        </div>
      </div>
      {loading ? (
        <div className="p-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center text-slate-400">No customer accounts yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Customer</th>
                <th className="px-4 py-3 font-semibold">Sign-in</th>
                <th className="px-4 py-3 font-semibold">Orders</th>
                <th className="px-4 py-3 font-semibold">Store Credit</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`customer-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-800">{u.name || "—"}</div>
                    <div className="text-xs text-slate-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{u.auth_provider === "google" ? "Google" : "Email"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.orders}</td>
                  <td className="px-4 py-3 font-semibold text-green-600" data-testid={`customer-credit-${u.id}`}>${Number(u.store_credit).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEdit(u)} data-testid={`customer-edit-${u.id}`}
                      className="text-xs font-semibold text-blue-600 border border-blue-200 hover:bg-blue-50 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <EditCustomer user={edit} onClose={() => setEdit(null)} onSaved={(nu) => { setUsers((p) => p.map((x) => (x.id === nu.id ? { ...x, ...nu } : x))); setEdit(null); }} />}
    </div>
  );
};

function EditCustomer({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [credit, setCredit] = useState(String(user.store_credit ?? 0));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${user.id}`, { name, store_credit: Number(credit) });
      toast.success("Customer updated");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-5" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="edit-customer-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2"><Wallet className="w-5 h-5 text-blue-600" /> Edit customer</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="text-xs text-slate-400 mb-3">{user.email}</div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-customer-name"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 mb-3" />
        <label className="text-xs font-medium text-slate-600 mb-1 block">Store Credit ($)</label>
        <input type="number" min="0" step="0.01" value={credit} onChange={(e) => setCredit(e.target.value)} data-testid="edit-customer-credit"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 mb-5" />
        <button onClick={save} disabled={saving} data-testid="edit-customer-save"
          className="brand-gradient text-white font-semibold w-full py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save changes"}</button>
      </div>
    </div>
  );
}
